import React from "react";
import { EditableText, EditableNumber, TrendControl, TrendIcon } from "@/components/fields";
import { OrnateFrame, Flourish } from "@/components/royal";
import { Button } from "@/components/ui/button";
import {
  Landmark, Crown, CalendarDays, Coins, Heart, Scale, Boxes, Gem,
  Trophy, Hammer, Plus, Trash2, PiggyBank,
} from "lucide-react";

function TextCard({ Icon, label, children, testId }) {
  return (
    <OrnateFrame hover testId={testId} className="px-6 pt-7 pb-6">
      <div className="flex items-center justify-between mb-3">
        <div className="ht-uppercase-label">{label}</div>
        <Icon strokeWidth={1.5} className="w-5 h-5 text-gold/70" />
      </div>
      {children}
    </OrnateFrame>
  );
}

function BigStat({ Icon, label, desc, num, trend, editable, onDesc, onNum, onTrend, testId }) {
  return (
    <OrnateFrame hover testId={testId} className="px-6 pt-7 pb-6">
      <div className="flex items-center justify-between mb-3">
        <div className="ht-uppercase-label">{label}</div>
        <Icon strokeWidth={1.5} className="w-5 h-5 text-gold/70" />
      </div>
      {editable ? (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <EditableNumber value={num} editable onChange={onNum} testId={`${testId}-num`} />
            <TrendControl value={trend} onChange={onTrend} testId={`${testId}-trend`} />
          </div>
          {onDesc && <EditableText value={desc} editable onChange={onDesc} placeholder="Descriptor" testId={`${testId}-desc`} />}
        </div>
      ) : (
        <>
          <div className="flex items-end gap-3">
            <span className="ht-title text-5xl leading-none tabular-nums" data-testid={`${testId}-num`}>{num ?? 0}</span>
            <span className="mb-1"><TrendIcon trend={trend} /></span>
          </div>
          {desc && <div className="text-sm text-muted-foreground mt-2">{desc}</div>}
        </>
      )}
    </OrnateFrame>
  );
}

export default function Overview({ player, updatePlayer, editable, addIncome }) {
  const set = (field) => (v) => updatePlayer((p) => { p[field] = v; });
  const charName = player.character?.name || "Ruler";

  const improvements = player.kingdom_improvements || [];
  const addImp = () => updatePlayer((p) => { p.kingdom_improvements = p.kingdom_improvements || []; p.kingdom_improvements.push({ id: crypto.randomUUID(), text: "" }); });
  const chgImp = (id, v) => updatePlayer((p) => { const e = p.kingdom_improvements.find((x) => x.id === id); if (e) e.text = v; });
  const rmImp = (id) => updatePlayer((p) => { p.kingdom_improvements = p.kingdom_improvements.filter((x) => x.id !== id); });

  const insp = player.inspiration_points || [];
  const addInsp = () => updatePlayer((p) => { p.inspiration_points = p.inspiration_points || []; p.inspiration_points.unshift({ id: crypto.randomUUID(), description: "" }); });
  const chgInsp = (id, v) => updatePlayer((p) => { const e = p.inspiration_points.find((x) => x.id === id); if (e) e.description = v; });
  const rmInsp = (id) => updatePlayer((p) => { p.inspiration_points = p.inspiration_points.filter((x) => x.id !== id); });

  return (
    <div className="space-y-8">
      <div>
        <div className="ht-eyebrow mb-3">{player.current_turn || "The Realm"}</div>
        <h1 className="ht-title text-4xl sm:text-6xl" data-testid="overview-greeting">Welcome back, {charName}</h1>
        <Flourish className="mt-5 max-w-sm" />
      </div>

      {/* Row 1 — identity */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <TextCard Icon={Landmark} label="Faction" testId="overview-kingdom-card">
          {editable
            ? <EditableText value={player.kingdom_name} editable onChange={set("kingdom_name")} testId="ov-kingdom" />
            : <div className="ht-title text-2xl" data-testid="ov-kingdom">{player.kingdom_name || "—"}</div>}
        </TextCard>
        <TextCard Icon={Crown} label="Ruler" testId="overview-ruler-card">
          {editable ? (
            <div className="space-y-2">
              <EditableText value={player.ruler_name} editable onChange={set("ruler_name")} placeholder="Ruler name" testId="ov-ruler-name" />
              <EditableText value={player.ruler_epithet} editable onChange={set("ruler_epithet")} placeholder="Epithet" testId="ov-ruler-epithet" />
            </div>
          ) : (
            <div className="text-lg" data-testid="ov-ruler-display">
              {player.ruler_name || "—"}{player.ruler_epithet ? ` (${player.ruler_epithet})` : ""}
            </div>
          )}
        </TextCard>
        <TextCard Icon={CalendarDays} label="Current Turn" testId="overview-turn-card">
          {editable
            ? <EditableText value={player.current_turn} editable onChange={set("current_turn")} placeholder="April, 1001 R.E." testId="ov-turn" />
            : <div className="ht-title text-2xl" data-testid="ov-turn">{player.current_turn || "—"}</div>}
        </TextCard>
      </div>

      {/* Row 2 — economy / morale / tax */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <BigStat Icon={Coins} label="Economy" desc={player.economy_desc} num={player.economy_num} trend={player.economy_trend}
          editable={editable} onDesc={set("economy_desc")} onNum={set("economy_num")} onTrend={set("economy_trend")} testId="ov-economy" />
        <BigStat Icon={Heart} label="Morale" desc={player.morale_desc} num={player.morale_num} trend={player.morale_trend}
          editable={editable} onDesc={set("morale_desc")} onNum={set("morale_num")} onTrend={set("morale_trend")} testId="ov-morale" />
        <BigStat Icon={Scale} label="Tax Rate" desc={player.tax_desc} num={player.tax_num} trend={player.tax_trend}
          editable={editable} onDesc={set("tax_desc")} onNum={set("tax_num")} onTrend={set("tax_trend")} testId="ov-tax" />
      </div>

      {/* Row 3 — resources / gold */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <BigStat Icon={Boxes} label="Resources in Storage" num={player.resources} trend={player.resources_trend}
          editable={editable} onNum={set("resources")} onTrend={set("resources_trend")} testId="ov-resources" />
        <BigStat Icon={Gem} label="Gold in Treasury" num={player.gold} trend={player.gold_trend}
          editable={editable} onNum={set("gold")} onTrend={set("gold_trend")} testId="ov-gold" />
      </div>

      {/* Admin-only treasury management (hidden from players) */}
      {editable && (
        <OrnateFrame className="px-6 pt-7 pb-6" testId="treasury-tool">
          <div className="flex items-center justify-between mb-4">
            <div className="ht-eyebrow">Treasury Management · Admin Only</div>
            <PiggyBank strokeWidth={1.5} className="w-5 h-5 text-gold/70" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
            <EditableNumber label="Monthly Gold Income" value={player.monthly_gold_income} editable onChange={set("monthly_gold_income")} testId="ov-monthly-gold" />
            <EditableNumber label="Monthly Resource Income" value={player.monthly_resource_income} editable onChange={set("monthly_resource_income")} testId="ov-monthly-resource" />
            <Button onClick={addIncome} className="rounded-sm font-heading" data-testid="add-income-button">
              <Plus strokeWidth={1.5} className="w-4 h-4 mr-2" /> Add Monthly Income
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            "Add Monthly Income" instantly credits this faction's Gold & Resources above. Then adjust the Gold/Resources numbers manually for any extra a turn earns. Players never see this panel.
          </p>
        </OrnateFrame>
      )}

      {/* Kingdom Improvements (compact) */}
      <OrnateFrame className="px-6 pt-7 pb-6" testId="overview-improvements">
        <div className="flex items-center justify-between mb-4">
          <div className="ht-eyebrow">Faction Improvements</div>
          {editable && (
            <Button variant="outline" size="sm" className="rounded-sm" onClick={addImp} data-testid="add-improvement">
              <Plus strokeWidth={1.5} className="w-4 h-4 mr-1" /> Add
            </Button>
          )}
        </div>
        {improvements.length === 0 ? (
          <p className="text-sm text-muted-foreground">No improvements recorded.</p>
        ) : (
          <div className="space-y-2" data-testid="improvements-list">
            {improvements.map((it, i) => (
              <div key={it.id} className="flex items-center gap-3" data-testid={`improvement-${i}`}>
                <Hammer strokeWidth={1.5} className="w-4 h-4 shrink-0 text-gold" />
                {editable ? (
                  <>
                    <div className="flex-1"><EditableText value={it.text} editable onChange={(v) => chgImp(it.id, v)} placeholder="e.g. Expanded the docks" testId={`improvement-text-${i}`} /></div>
                    <Button variant="ghost" size="icon" className="rounded-sm h-8 w-8 text-destructive" onClick={() => rmImp(it.id)} data-testid={`improvement-remove-${i}`}>
                      <Trash2 strokeWidth={1.5} className="w-4 h-4" />
                    </Button>
                  </>
                ) : (
                  <span className="text-sm">{it.text}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </OrnateFrame>

      {/* Inspiration Points */}
      <OrnateFrame className="px-6 pt-7 pb-6" testId="overview-inspiration">
        <div className="flex items-center justify-between mb-4">
          <div className="ht-eyebrow">Inspiration Points</div>
          {editable && (
            <Button variant="outline" size="sm" className="rounded-sm" onClick={addInsp} data-testid="add-inspiration">
              <Plus strokeWidth={1.5} className="w-4 h-4 mr-1" /> Add
            </Button>
          )}
        </div>
        {insp.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="inspiration-empty">No inspiration points yet.</p>
        ) : (
          <div className="space-y-3" data-testid="inspiration-list">
            {insp.map((e, i) => (
              <div key={e.id} className="flex items-start gap-3" data-testid={`inspiration-${i}`}>
                <div className="w-8 h-8 border border-gold/40 rounded-sm flex items-center justify-center shrink-0">
                  <Trophy strokeWidth={1.5} className="w-4 h-4 text-gold" />
                </div>
                <div className="flex-1">
                  {editable
                    ? <EditableText value={e.description} editable onChange={(v) => chgInsp(e.id, v)} placeholder="Describe the clever play…" testId={`inspiration-text-${i}`} />
                    : <p className="text-base leading-relaxed">{e.description}</p>}
                </div>
                {editable && (
                  <Button variant="ghost" size="icon" className="rounded-sm h-8 w-8 text-destructive" onClick={() => rmInsp(e.id)} data-testid={`inspiration-remove-${i}`}>
                    <Trash2 strokeWidth={1.5} className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </OrnateFrame>
    </div>
  );
}
